import { z } from '@hono/zod-openapi';

import { ChannelSchema, InstantSchema, UUIDSchema } from './common';

export const RecoverySecretSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{43}$/)
  .openapi({ description: '256-bit base64url secret; write-only and never logged' });

export const DeviceCredentialSchema = z
  .string()
  .regex(/^pnd1\.[0-9a-f-]{36}\.[1-9][0-9]*\.[A-Za-z0-9_-]{43}$/i)
  .openapi({ description: 'One-time returned opaque device credential; never logged' });

export const IssueDeviceRequestSchema = z
  .object({
    platform: ChannelSchema,
    turnstileToken: z.string().min(1).max(2_048),
  })
  .strict()
  .openapi('IssueDeviceRequest');

export const TossContinuityResponseSchema = z
  .object({ connected: z.boolean() })
  .strict()
  .openapi('TossContinuityResponse');

export const DeviceCredentialResponseSchema = z
  .object({
    deviceId: UUIDSchema,
    credential: DeviceCredentialSchema,
    credentialVersion: z.number().int().positive(),
    issuedAt: InstantSchema,
  })
  .strict()
  .openapi('DeviceCredentialResponse');

export const RegisterBackupAccountRequestSchema = z
  .object({
    backupId: UUIDSchema,
    authVersion: z.literal(1),
    authSecret: RecoverySecretSchema,
  })
  .strict()
  .openapi('RegisterBackupAccountRequest');

export const RotateBackupAuthRequestSchema = z
  .object({
    backupId: UUIDSchema,
    currentAuthVersion: z.number().int().positive(),
    currentAuthSecret: RecoverySecretSchema,
    nextAuthSecret: RecoverySecretSchema,
  })
  .strict()
  .refine((value) => value.currentAuthSecret !== value.nextAuthSecret, {
    message: 'nextAuthSecret must differ',
    path: ['nextAuthSecret'],
  })
  .openapi('RotateBackupAuthRequest');

export const LinkBackupAccountRequestSchema = z
  .object({
    backupId: UUIDSchema,
    authVersion: z.number().int().positive(),
    authSecret: RecoverySecretSchema,
  })
  .strict()
  .openapi('LinkBackupAccountRequest');

export const BackupAccountResponseSchema = z
  .object({
    backupId: UUIDSchema,
    authVersion: z.number().int().positive(),
    linkedDeviceId: UUIDSchema.optional(),
    updatedAt: InstantSchema,
  })
  .strict()
  .openapi('BackupAccountResponse');

export type IssueDeviceRequest = z.infer<typeof IssueDeviceRequestSchema>;
export type TossContinuityResponse = z.infer<typeof TossContinuityResponseSchema>;
export type DeviceCredentialResponse = z.infer<typeof DeviceCredentialResponseSchema>;
export type RegisterBackupAccountRequest = z.infer<typeof RegisterBackupAccountRequestSchema>;
export type RotateBackupAuthRequest = z.infer<typeof RotateBackupAuthRequestSchema>;
export type LinkBackupAccountRequest = z.infer<typeof LinkBackupAccountRequestSchema>;
export type BackupAccountResponse = z.infer<typeof BackupAccountResponseSchema>;
